import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { SUPPORT_CONCERN_OPTIONS, type SupportConcernType } from '../constants/support';

type SubmitResponse = {
  id: string;
  concernType: string;
  status: string;
  createdAt: string;
};

function IconPaperPlane({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
      />
    </svg>
  );
}

export function HelpSupportPage() {
  const navigate = useNavigate();
  const [concernType, setConcernType] = useState<SupportConcernType>('TECHNICAL');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    const trimmed = message.trim();
    if (!trimmed) {
      setError('Please describe your concern.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post<SubmitResponse>('/api/support-requests', {
        concernType,
        message: trimmed,
      });
      if (!res.success) {
        setError(res.error || 'Could not submit.');
        return;
      }
      window.alert('Thank you. Your query has been submitted. Our team will get back to you.');
      navigate(-1);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not submit. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pb-28">
      <div className="mb-6 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100"
          aria-label="Back"
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-black text-slate-900">Help &amp; Support</h1>
      </div>

      <div className="mb-8 flex gap-4 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-100">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-emerald-100">
          <span className="text-2xl font-bold text-emerald-700">?</span>
        </div>
        <div>
          <h2 className="text-xl font-black text-slate-900">Help &amp; Support</h2>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">
            Reach out to our agents
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <label htmlFor="concern" className="mb-2 block text-[11px] font-bold uppercase tracking-wide text-blue-600">
            Select issue category
          </label>
          <select
            id="concern"
            value={concernType}
            onChange={(e) => setConcernType(e.target.value as SupportConcernType)}
            className="w-full rounded-xl border-0 bg-slate-100 px-4 py-3.5 text-sm font-semibold text-slate-900 outline-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500"
          >
            {SUPPORT_CONCERN_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="message" className="mb-2 block text-[11px] font-bold uppercase tracking-wide text-blue-600">
            Describe your problem
          </label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Please provide as much detail as possible..."
            rows={8}
            className="w-full resize-y rounded-xl border-0 bg-slate-100 px-4 py-3 text-sm text-slate-900 outline-none ring-1 ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {error ? <p className="text-sm font-semibold text-red-600">{error}</p> : null}

        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-[#1e3a5f] py-4 text-sm font-bold text-white shadow-md transition-opacity hover:opacity-95 disabled:opacity-50"
        >
          <IconPaperPlane className="h-5 w-5 text-white" />
          {submitting ? 'Submitting…' : 'Submit Query'}
        </button>
      </div>
    </div>
  );
}
